import { Tag } from 'antd';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { isHasAccess } from 'features/validateAccess';
import { ProjectDTO } from 'entities/project';
import { ReactComponent as TimesheetsIcon } from 'shared/assets/icons/dynamicColor/corner_down_right_arrow_icon.svg';
import { pathKeys } from 'shared/lib/react-router';
import { LinkButton } from 'shared/ui/LinkButton';
import { ProtectedLink } from 'shared/ui/ProtectedLink';
import { formatProjectStatus } from '../../lib';
import { getProjectViewTimesheetPermission } from '../../ProjectList.permissions';

import styles from './ProjectItem.module.scss';

export interface ProjectItemProps {
  accountId: ProjectDTO['accountId'];
  projectId: ProjectDTO['id'];
  name: ProjectDTO['name'];
  positionCount: number;
  isActive: ProjectDTO['active'];
  hideEditButton: boolean;
  disableLink: boolean;
  archived: ProjectDTO['archived'];
}

const ProjectItem: React.FC<ProjectItemProps> = ({
  accountId,
  projectId,
  name,
  disableLink,
  isActive,
  positionCount,
  hideEditButton,
  archived,
}) => {
  const { t } = useTranslation('Project');
  const isAllowedToViewTimesheets = isHasAccess(getProjectViewTimesheetPermission(projectId));
  return (
    <li className={styles.projectItem}>
      <div className={styles.projectInfo}>
        <div className={styles.nameWrapper}>
          <ProtectedLink
            hasAccess={!disableLink}
            className={classNames(styles.name, {
              [styles.muted]: archived || !isActive,
              [styles.disabled]: disableLink,
            })}
            to={pathKeys.position.root({ accountId, projectId })}
          >
            {name}
          </ProtectedLink>
        </div>
        <div className={styles.statusBadge}>
          <span className={styles.statusLabel}>{t('ProjectList.status')}:</span>
          <Tag
            className={classNames({
              [styles.archived]: archived,
              [styles.active]: !archived && isActive,
              [styles.nonActive]: !archived && !isActive,
            })}
          >
            {formatProjectStatus(isActive, archived, t)}
          </Tag>
        </div>
        <div className={classNames(styles.positionsCounter, { [styles.archived]: archived })}>
          <span className={styles.positionsCounterLabel}>{t('ProjectList.activePositions')}:</span>
          <span>{positionCount}</span>
        </div>
      </div>
      <div className={styles.actionButtons}>
        {isAllowedToViewTimesheets ? (
          <LinkButton
            buttonProps={{
              icon: <TimesheetsIcon className={styles.timesheetsButtonIcon} />,
              className: styles.timesheetsButton,
              disabled: archived,
            }}
            linkProps={{ to: `${pathKeys.timesheets()}?accountId=${accountId}&projectId=${projectId}` }}
          >
            {t('ProjectItem.timesheetsBtn.text')}
          </LinkButton>
        ) : null}
        {!hideEditButton && (
          <LinkButton
            linkProps={{ to: pathKeys.project.update({ accountId, projectId }) }}
            buttonProps={{ disabled: archived }}
          >
            {t('Shared:editBtn.text')}
          </LinkButton>
        )}
      </div>
    </li>
  );
};

export default ProjectItem;
